import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, ShoppingCart, Loader2, ShieldCheck, MapPin, Phone, Info } from "lucide-react";

const inputCls = "min-w-0 rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 bg-white";
const fmt = (p) => (p || "").replace(/^\+1(\d{3})(\d{3})(\d{4})$/, "($1) $2-$3");

// Search US numbers (local by area code, or toll-free) and buy one: purchase → SMS/voice webhooks
// wired → added to the verified A2P Messaging Service → optionally assigned to a community.
// `tenants` (optional) offers the assign-to picker; `onBought(result)` fires after a purchase.
export function BuyNumberPanel({ tenants = [], defaultTenantId = "", onBought, compact = false, testid = "buy-number" }) {
  const [kind, setKind] = useState("local");
  const [areaCode, setAreaCode] = useState("");
  const [contains, setContains] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [buying, setBuying] = useState("");
  const [tenantId, setTenantId] = useState(defaultTenantId);
  const [confirm, setConfirm] = useState(null);

  const search = async () => {
    if (kind === "local" && areaCode.replace(/\D/g, "").length !== 3) { toast.error("Enter a 3-digit area code"); return; }
    setSearching(true);
    try {
      const r = await api.get("/admin/twilio/numbers/search", { params: { kind, area_code: kind === "local" ? areaCode : "", contains, limit: 12 } });
      setResults(r.data);
      if (r.data.error) toast.error(`Twilio: ${r.data.error}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Search failed"); }
    finally { setSearching(false); }
  };

  const buy = async (n) => {
    setBuying(n.phone_number);
    try {
      const r = await api.post("/admin/twilio/numbers/buy", { phone_number: n.phone_number, tenant_id: tenantId || undefined, base_url: window.location.origin });
      const d = r.data;
      const a2p = d.a2p?.added ? ` · added to ${d.a2p.service_name || "the A2P service"}` : d.a2p?.reason ? ` · A2P: ${d.a2p.reason}` : "";
      toast.success(`${d.dry_run ? "Simulated purchase (preview) — " : ""}${fmt(d.phone_number)} is wired${d.assigned_tenant_name ? ` and assigned to ${d.assigned_tenant_name}` : ""}${a2p}`);
      setConfirm(null);
      setResults((res) => res ? { ...res, numbers: res.numbers.filter((x) => x.phone_number !== n.phone_number) } : res);
      onBought?.(d);
    } catch (e) { toast.error(e.response?.data?.detail || "Twilio refused the purchase"); }
    finally { setBuying(""); }
  };

  const svc = results?.a2p_service;
  const fb = results?.fallback;
  const tenantName = tenants.find((t) => t.id === tenantId)?.name;
  const wrap = compact ? "" : "rounded-2xl border border-sand-200 bg-white p-4 sm:p-5 md:px-6";

  return (
    <div data-testid={`${testid}-panel`} className={wrap}>
      {!compact && (
        <div className="flex items-start gap-3 mb-4">
          <div className="h-9 w-9 rounded-xl bg-redline-50 flex items-center justify-center shrink-0"><ShoppingCart className="h-[18px] w-[18px] text-redline-500" strokeWidth={1.5} /></div>
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-[15px] text-ink">Buy a number</h3>
            <p className="text-sm text-muted-foreground mt-0.5">Search, buy, done: the number is wired for texts and calls, joins the verified A2P campaign, and lands on the community you pick.</p>
          </div>
        </div>
      )}

      {/* Row 1 — what kind, where */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-xl border border-sand-200 p-0.5 bg-sand-50 shrink-0">
          {[["local", "Local"], ["toll_free", "Toll-free"]].map(([k, l]) => (
            <button key={k} type="button" data-testid={`${testid}-kind-${k}`} onClick={() => { setKind(k); setResults(null); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${kind === k ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>{l}</button>
          ))}
        </div>
        {kind === "local" && (
          <input data-testid={`${testid}-area-code`} value={areaCode} onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))}
            placeholder="Area code" inputMode="numeric" className={inputCls + " w-28"} onKeyDown={(e) => e.key === "Enter" && search()} />
        )}
        <button type="button" data-testid={`${testid}-search`} onClick={search} disabled={searching}
          className="rounded-full bg-ink text-white px-4 py-2.5 text-sm font-semibold hover:bg-ink-700 disabled:opacity-40 inline-flex items-center gap-1.5 shrink-0">
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search
        </button>
      </div>
      {/* Row 2 — optional refinements */}
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <input data-testid={`${testid}-contains`} value={contains} onChange={(e) => setContains(e.target.value)} placeholder="Digits it should contain (optional)" className={inputCls + " w-full"} />
        {tenants.length > 0 && (
          <select data-testid={`${testid}-tenant`} value={tenantId} onChange={(e) => setTenantId(e.target.value)} className={inputCls + " w-full"}>
            <option value="">Buy without assigning</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{`Assign to ${t.name}${t.phone_number ? ` (replaces ${fmt(t.phone_number)})` : ""}`}</option>)}
          </select>
        )}
      </div>

      {results && (
        <div className="mt-4 space-y-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 text-xs text-ink/60" data-testid={`${testid}-a2p-line`}>
            <span className="inline-flex items-start gap-1.5"><ShieldCheck className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${svc ? "text-emerald-600" : "text-amber-600"}`} />
              {svc ? <span>Purchases join <b>{svc.name}</b> (verified A2P campaign) automatically.</span> : <span>No Messaging Service with a verified campaign found — numbers are bought and wired, but not registered.</span>}</span>
            {results.dry_run && <span className="self-start sm:ml-auto rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 font-semibold whitespace-nowrap">Preview — purchases are simulated</span>}
          </div>
          {fb && (
            <div className="flex items-start gap-2 rounded-xl bg-sky-50 border border-sky-100 px-3 py-2 text-xs text-sky-900" data-testid={`${testid}-fallback-note`}>
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              {fb.kind === "topup" ? (
                <span>Only <b>{fb.exact}</b> number{fb.exact === 1 ? " is" : "s are"} left in <b>{fb.requested}</b> — it&apos;s all but sold out at Twilio.
                  Listed first, then <b>{(fb.area_codes || []).join(" / ")}</b>, the overlay code{(fb.area_codes || []).length > 1 ? "s" : ""} for the same area (same city, same local calls).</span>
              ) : (
                <span>No <b>{fb.requested}</b> numbers are available right now — {fb.kind === "overlay"
                  ? <>showing <b>{fb.area_codes.join(" / ")}</b>, the overlay code{fb.area_codes.length > 1 ? "s" : ""} for the same area (same city, same local calls).</>
                  : <>showing other <b>{fb.region}</b> numbers instead.</>}</span>
              )}
            </div>
          )}
          {results.numbers?.length === 0 && (
            <div className="text-sm text-ink/60 py-3" data-testid={`${testid}-empty`}>
              {results.error ? `Twilio couldn't search: ${results.error}` : `Nothing available for ${kind === "local" ? `area code ${areaCode}` : "toll-free"} right now${contains ? ` containing “${contains}”` : ""}. Try a neighbouring area code or clear the digits filter.`}
            </div>
          )}
          <div className="grid gap-1.5 sm:grid-cols-2">
            {(results.numbers || []).map((n) => {
              const id = n.phone_number.replace("+", "");
              const isConfirm = confirm === n.phone_number;
              return (
                <div key={n.phone_number} data-testid={`${testid}-result-${id}`} className={`rounded-xl border px-3.5 py-2.5 ${isConfirm ? "border-redline-300 bg-redline-50/40" : "border-sand-200"}`}>
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-ink/40 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-sm font-semibold">{fmt(n.phone_number)}</div>
                      <div className="text-[11px] text-ink/50 inline-flex items-center gap-1 flex-wrap">
                        {(n.locality || n.region) && <><MapPin className="h-3 w-3" />{[n.locality, n.region].filter(Boolean).join(", ")}</>}
                        {n.via === "overlay" && <span className="rounded-full bg-sky-100 text-sky-800 px-1.5 py-px font-semibold">overlay</span>}
                      </div>
                    </div>
                    {!isConfirm && (
                      <button type="button" data-testid={`${testid}-buy-${id}`} onClick={() => setConfirm(n.phone_number)}
                        className="rounded-full border border-sand-200 px-3 py-1.5 text-xs font-semibold hover:border-ink shrink-0">Buy</button>
                    )}
                  </div>
                  {isConfirm && (
                    <div className="mt-2.5 pt-2.5 border-t border-redline-100 flex flex-wrap items-center gap-2 text-xs">
                      <span className="text-ink/70 flex-1 min-w-[10rem]">{tenantName ? <>Assign to <b>{tenantName}</b> and wire it now.</> : "Buy and wire it; assign later from the list."}</span>
                      <button type="button" data-testid={`${testid}-confirm-${id}`} disabled={!!buying} onClick={() => buy(n)}
                        className="rounded-full bg-redline-500 text-white px-3 py-1.5 font-semibold hover:bg-redline-600 disabled:opacity-50 inline-flex items-center gap-1">
                        {buying === n.phone_number ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Confirm purchase
                      </button>
                      <button type="button" onClick={() => setConfirm(null)} className="font-semibold text-ink/60 hover:text-ink px-2">Cancel</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
